import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { logUxEvent } from './telemetry/uxLogger';
import ProgressBar from './components/ProgressBar';
import FormBackButton from './components/FormBackButton';
import DecoDivider from './components/DecoDivider';
import useViewportInsets from './hooks/useViewportInsets';
import useFormSubmitGuard from './hooks/useFormSubmitGuard';
import {
  DEFAULT_PHONE_DIAL,
  PRESET_COUNTRY_DIALS,
  STORAGE_KEY_PHONE,
  STORAGE_KEY_PHONE_DIAL,
  isPresetDial,
  isValidBrazilDdd,
  writePatientPhoneDial,
} from './utils/patientPhoneStorage';
import {
  isSignupFieldTypedHere,
  markSignupFieldTyped,
} from './utils/signupFieldPrefill';
import { persistIdentityKey } from './utils/identityBackup';
import {
  nextSignupRoute,
  prevReachableSignupRoute,
  getSignupVariant,
  isFirstStep,
} from './utils/signupOrderAb';
import { ensureSignupSession } from './utils/ensureSignupSession';
import { takeSignupQuickResumeStep, cancelSignupQuickResume } from './utils/signupQuickResume';
import { awaitPendingRegistrationStart, currentSignupNavGeneration } from './utils/emailStepRegistration';
import {
  usesGuidedCopy,
  usesInlineTerms,
  signupUxProgress,
} from './utils/signupUxAb';
import { signupCta, signupStepTitle } from './utils/signupUxCopy';
import FieldValidCheck, { FIELD_VALID_CHECK_CSS } from './components/FieldValidCheck';
import SignupStepHeading from './components/SignupStepHeading';
import SignupGreeting, { SIGNUP_GREETING_CSS, readSignupFullName } from './components/SignupGreeting';
import SignupTermsInlineNotice, { SIGNUP_TERMS_INLINE_CSS } from './components/SignupTermsInlineNotice';
import ImageFraudTacModal from './components/ImageFraudTacModal';
import LevelUpScreen from './components/LevelUpScreen';
import {
  SIGNUP_TERMS_API_URL,
  SIGNUP_TERMS_BLOCKED_DEFAULT,
  useSignupTermsAccept,
} from './hooks/useSignupTermsAccept';

const OTHER_DIAL_VALUE = '__other__';

const FLAG_MAP = {
  '55': '\uD83C\uDDE7\uD83C\uDDF7',
  '351': '\uD83C\uDDF5\uD83C\uDDF9',
  '1': '\uD83C\uDDFA\uD83C\uDDF8',
  '54': '\uD83C\uDDE6\uD83C\uDDF7',
  '598': '\uD83C\uDDFA\uD83C\uDDFE',
  '595': '\uD83C\uDDF5\uD83C\uDDFE',
  '56': '\uD83C\uDDE8\uD83C\uDDF1',
  '57': '\uD83C\uDDE8\uD83C\uDDF4',
  '52': '\uD83C\uDDF2\uD83C\uDDFD',
  '49': '\uD83C\uDDE9\uD83C\uDDEA',
  '33': '\uD83C\uDDEB\uD83C\uDDF7',
  '39': '\uD83C\uDDEE\uD83C\uDDF9',
  '34': '\uD83C\uDDEA\uD83C\uDDF8',
  '44': '\uD83C\uDDEC\uD83C\uDDE7',
};

function getFlag(dial) {
  return FLAG_MAP[String(dial || '')] || '\uD83C\uDF10';
}

const COUNTRY_LIST = PRESET_COUNTRY_DIALS.map((c) => ({
  dial: c.dial,
  label: c.label,
  flag: getFlag(c.dial),
}));

const stripLeadingBrazilCountryCode = (raw) => {
  let d = String(raw || '').replace(/\D/g, '');
  let guard = 0;
  while (d.length > 11 && d.startsWith('55') && guard < 6) {
    d = d.slice(2);
    guard += 1;
  }
  return d;
};

const formatPhoneBR = (raw) => {
  const digits = stripLeadingBrazilCountryCode(raw).slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 6) return '(' + digits.slice(0, 2) + ') ' + digits.slice(2);
  if (digits.length <= 10) return '(' + digits.slice(0, 2) + ') ' + digits.slice(2, 6) + '-' + digits.slice(6);
  return '(' + digits.slice(0, 2) + ') ' + digits.slice(2, 7) + '-' + digits.slice(7);
};

function getEffectiveDial(dialSelect, otherDial) {
  if (dialSelect === OTHER_DIAL_VALUE) {
    const o = String(otherDial || '').replace(/\D/g, '').slice(0, 3);
    if (!o || !/^[1-9]\d{0,2}$/.test(o)) return '';
    return o;
  }
  return dialSelect;
}

function readSessionDigits(key) {
  try {
    return String(sessionStorage.getItem(key) || '').replace(/\D/g, '');
  } catch {
    return '';
  }
}

/**
 * Telefone que esta aba digitou, no formato que o campo espera: máscara
 * brasileira quando o DDI é o do Brasil, dígitos crus nos outros países.
 */
function readSessionPhoneState() {
  const empty = { dialSelect: DEFAULT_PHONE_DIAL, otherDial: '', phone: '' };
  // O DDI não tem marca própria: sem telefone ele não significa nada.
  if (!isSignupFieldTypedHere(STORAGE_KEY_PHONE)) return empty;
  const national = readSessionDigits(STORAGE_KEY_PHONE);
  if (!national) return empty;
  const dial = readSessionDigits(STORAGE_KEY_PHONE_DIAL);
  if (!dial || dial === DEFAULT_PHONE_DIAL) {
    return { dialSelect: DEFAULT_PHONE_DIAL, otherDial: '', phone: formatPhoneBR(national) };
  }
  // Mesmo teto do `handleChange`: o E.164 cabe em 15 dígitos, DDI incluído.
  const phone = national.slice(0, 15 - dial.length);
  return isPresetDial(dial)
    ? { dialSelect: dial, otherDial: '', phone }
    : { dialSelect: OTHER_DIAL_VALUE, otherDial: dial, phone };
}

function stripLeadingDialFromPasted(pastedDigits, dial) {
  let d = String(pastedDigits || '').replace(/\D/g, '');
  if (!dial || d.length < dial.length + 4) return d;
  if (d.startsWith(dial)) d = d.slice(dial.length);
  return d;
}

const PhoneForm = () => {
  const navigate = useNavigate();
  useViewportInsets();
  const guided = usesGuidedCopy();
  const inlineTerms = usesInlineTerms();
  const {
    acceptTerms,
    showLevelUp,
    blockedMessage,
    setBlockedMessage,
    acceptError,
    tacInfo,
    setTacInfo,
    loadingTrap,
    goAfterAccept,
    levelUpCopy,
    patientId: termsPatientId,
  } = useSignupTermsAccept();

  // Voltar no funil relê o telefone desta sessão (com o DDI escolhido);
  // consulta nova começa vazia, no DDI do Brasil.
  const sessionPhone = useMemo(readSessionPhoneState, []);
  const [dialSelect, setDialSelect] = useState(sessionPhone.dialSelect);
  const [otherDial, setOtherDial] = useState(sessionPhone.otherDial);
  const [phone, setPhone] = useState(sessionPhone.phone);
  const [touched, setTouched] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState('');

  const inputRef = useRef(null);
  const otherDialRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  const effectiveDial = useMemo(() => getEffectiveDial(dialSelect, otherDial), [dialSelect, otherDial]);
  const isBrazilDial = effectiveDial === DEFAULT_PHONE_DIAL;
  const dialValid = dialSelect !== OTHER_DIAL_VALUE || !!effectiveDial;

  const rawNational = useMemo(() => {
    if (isBrazilDial) return stripLeadingBrazilCountryCode(phone).slice(0, 11);
    if (!effectiveDial) return '';
    return phone.replace(/\D/g, '').slice(0, 15 - effectiveDial.length);
  }, [phone, isBrazilDial, effectiveDial]);

  const fullDigits = useMemo(() => {
    if (!effectiveDial || !rawNational) return '';
    return effectiveDial + rawNational;
  }, [effectiveDial, rawNational]);

  const isValid = useMemo(() => {
    if (!dialValid || !effectiveDial) return false;
    if (isBrazilDial) {
      return rawNational.length >= 10 && rawNational.length <= 11 && isValidBrazilDdd(rawNational);
    }
    const len = fullDigits.length;
    return len >= 10 && len <= 15;
  }, [dialValid, effectiveDial, isBrazilDial, rawNational, fullDigits]);

  const filteredCountries = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return COUNTRY_LIST;
    return COUNTRY_LIST.filter(
      (c) => c.label.toLowerCase().includes(q) || c.dial.includes(q)
    );
  }, [search]);

  useEffect(() => {
    if (!dropdownOpen) return;
    setTimeout(() => searchRef.current?.focus(), 60);
    const handler = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setDropdownOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [dropdownOpen]);

  const handleConfirmRaw = async () => {
    const navGen = currentSignupNavGeneration();
    setTouched(true);
    if (!isValid || !effectiveDial) {
      logUxEvent('phone_validation_error', {
        reason: 'invalid_phone',
        dial: effectiveDial || 'empty',
        length: fullDigits.length,
      });
      return false;
    }
    writePatientPhoneDial(effectiveDial, { omitIfBrazil: true });
    try {
      sessionStorage.setItem(STORAGE_KEY_PHONE, rawNational);
      localStorage.setItem(STORAGE_KEY_PHONE, rawNational);
    } catch {}
    // Backup multi-camada (IDB + cookie) — defesa contra perda de LS.
    try { persistIdentityKey('patientPhone', rawNational); } catch {}
    markSignupFieldTyped(STORAGE_KEY_PHONE);
    logUxEvent('phone_confirmed', { length: fullDigits.length, dial: effectiveDial });
    const session = await ensureSignupSession().catch(() => null);
    const patientId =
      (session && session.patientId) ||
      sessionStorage.getItem('patientId') ||
      localStorage.getItem('patientId') ||
      '';
    if (patientId) {
      const url = (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/+$/, '');
      awaitPendingRegistrationStart()
        .then(() => fetch(url + '/registration/step', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            patientId: sessionStorage.getItem('patientId') || patientId,
            step: 'telefone',
            phone: fullDigits,
          }),
        }))
        .catch(() => {});
    }
    if (navGen !== currentSignupNavGeneration()) return true;

    if (inlineTerms) {
      return acceptTerms({ source: 'phone_inline', signup_step: 'telefone' });
    }

    const variant = getSignupVariant();
    const dest = nextSignupRoute('telefone', variant) || '/termos';
    navigate(dest);
    return true;
  };

  const [isSubmitting, handleConfirm] = useFormSubmitGuard(handleConfirmRaw, {
    cooldownMs: inlineTerms ? 2500 : 1500,
    holdUntilSettled: inlineTerms,
  });

  // "Continuar como" na tela do nome: telefone salvo confirma sozinho, exceto
  // quando o Continuar daqui também aceita os Termos — aceite é do paciente.
  useEffect(() => {
    if (!takeSignupQuickResumeStep('telefone')) return;
    if (!isValid || inlineTerms || blockedMessage) {
      cancelSignupQuickResume();
      return;
    }
    logUxEvent('signup_quick_resume_step', { step: 'telefone' });
    handleConfirm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    setPhone(isBrazilDial ? formatPhoneBR(v) : v.replace(/\D/g, '').slice(0, effectiveDial ? 15 - effectiveDial.length : 12));
    if (!touched) setTouched(true);
  };

  const handlePaste = (e) => {
    const pDigits = (e.clipboardData?.getData('text') || '').replace(/\D/g, '');
    if (!pDigits) return;
    e.preventDefault();
    if (isBrazilDial) {
      const current = phone.replace(/\D/g, '');
      const next = pDigits.length >= 8
        ? stripLeadingBrazilCountryCode(pDigits).slice(0, 11)
        : stripLeadingBrazilCountryCode(current + pDigits).slice(0, 11);
      setPhone(formatPhoneBR(next));
    } else {
      setPhone(stripLeadingDialFromPasted(pDigits, effectiveDial).slice(0, effectiveDial ? 15 - effectiveDial.length : 12));
    }
    if (!touched) setTouched(true);
  };

  const selectCountry = (dial) => {
    const prevNat = isBrazilDial ? stripLeadingBrazilCountryCode(phone) : phone.replace(/\D/g, '');
    setDropdownOpen(false);
    setSearch('');
    if (dial === OTHER_DIAL_VALUE) {
      setDialSelect(OTHER_DIAL_VALUE);
      setPhone(prevNat.slice(0, 12));
      setTimeout(() => otherDialRef.current?.focus(), 80);
    } else {
      setDialSelect(dial);
      setOtherDial('');
      setPhone(dial === DEFAULT_PHONE_DIAL ? formatPhoneBR(prevNat) : prevNat.slice(0, 15 - dial.length));
      setTimeout(() => inputRef.current?.focus(), 80);
    }
    if (!touched) setTouched(true);
  };

  const handleOtherDialChange = (e) => {
    const o = e.target.value.replace(/\D/g, '').slice(0, 3);
    setOtherDial(o);
    setPhone((prev) => {
      const nat = prev.replace(/\D/g, '');
      const eff = getEffectiveDial(OTHER_DIAL_VALUE, o);
      return nat.slice(0, eff ? 15 - eff.length : 12);
    });
    if (!touched) setTouched(true);
  };

  const getErrorMessage = () => {
    if (!touched) return '\u00a0';
    if (dialSelect === OTHER_DIAL_VALUE && !effectiveDial) {
      return guided ? 'Falta o código DDI do país.' : 'Selecione o país e informe o código DDI.';
    }
    if (!rawNational) return guided ? 'Falta o número.' : 'Informe seu telefone.';
    if (isBrazilDial && rawNational.length < 10) return 'Inclua o DDD e o número completo.';
    if (isBrazilDial && !isValidBrazilDdd(rawNational)) return 'DDD inválido. Confira os dois primeiros dígitos.';
    if (!isBrazilDial && fullDigits.length < 10) return 'Número incompleto.';
    if (!isValid) return 'Telefone inválido. Verifique os números informados.';
    return '\u00a0';
  };

  const nationalMaxLen = isBrazilDial ? 15 : effectiveDial ? 15 - effectiveDial.length : 12;
  const currentFlag = dialSelect === OTHER_DIAL_VALUE ? getFlag('') : getFlag(effectiveDial);
  const currentDialLabel = effectiveDial ? ('+' + effectiveDial) : '+\u2014';

  if (loadingTrap) {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10600,
          background: '#F9FAFB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <style>{`@keyframes terms-trap-spin { to { transform: rotate(360deg); } }`}</style>
        <div
          aria-hidden="true"
          style={{
            width: '3rem',
            height: '3rem',
            border: '4px solid rgba(211,47,47,0.15)',
            borderTopColor: '#D32F2F',
            borderRadius: '50%',
            animation: 'terms-trap-spin 0.9s linear infinite',
          }}
        />
      </div>
    );
  }

  if (showLevelUp) {
    const copy = levelUpCopy();
    return (
      <LevelUpScreen
        level={1}
        title={copy.title}
        subtitle={copy.subtitle}
        duration={1800}
        onComplete={goAfterAccept}
      />
    );
  }

  return (
    <>
      <style>{`
        ${FIELD_VALID_CHECK_CSS}
        ${SIGNUP_GREETING_CSS}
        ${SIGNUP_TERMS_INLINE_CSS}
        :root {
          --primary: #D32F2F;
          --bg-page: #F9FAFB;
          --surface: #FFFFFF;
          --text-main: #1E1E1E;
          --text-dim: #6B6B6B;
          --border-subtle: #E5E7EB;
          --error: #D32F2F;
          --radius-lg: 1rem;
          --radius-xl: 1.5rem;
          --radius-full: 999px;
          --shadow-xs: 0 1px 2px rgba(0,0,0,0.04);
          --shadow-sm: 0 6px 16px rgba(0,0,0,0.06);
        }
        body {
          margin: 0;
          background: var(--bg-page);
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Inter', 'Roboto', sans-serif;
          color: var(--text-main);
          -webkit-font-smoothing: antialiased;
        }
        .page-shell {
          min-height: 100vh;
          min-height: 100dvh;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          background: var(--bg-page);
        }
        .inner-wrap {
          flex: 1;
          max-width: 480px;
          width: 100%;
          margin: 0 auto;
          padding: 4.5rem 1rem max(2rem, calc(1rem + var(--vvb, 0px) + env(safe-area-inset-bottom, 0px)));
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 1.25rem;
          box-sizing: border-box;
          transition: padding-bottom 0.18s ease;
        }
        .sr-only {
          position: absolute; width: 1px; height: 1px; padding: 0;
          margin: -1px; overflow: hidden; clip: rect(0,0,0,0);
          white-space: nowrap; border: 0;
        }
        .header-block {
          text-align: center; max-width: 22rem;
          margin-top: 1rem; margin-bottom: 2rem;
        }
        .main-headline {
          font-size: 1.25rem; line-height: 1.35; font-weight: 600;
          color: var(--text-main); margin: 0 0 .75rem;
        }
        .sub-headline {
          font-size: .9rem; line-height: 1.4; font-weight: 400;
          color: var(--text-dim); margin: 0.85rem auto 0;
        }

        .form-card {
          width: 100%; max-width: 400px;
          background: var(--surface);
          border-radius: var(--radius-xl);
          box-shadow: var(--shadow-sm);
          padding: 1.5rem 1.25rem 1.1rem;
          border: 1px solid rgba(0,0,0,0.06);
          display: flex; flex-direction: column; align-items: stretch;
          box-sizing: border-box;
          overflow: visible;
        }

        /* ── Phone field ── */
        .phone-field-wrap {
          position: relative;
          display: flex;
          align-items: stretch;
          border: 1.5px solid var(--border-subtle);
          border-radius: 0.75rem;
          background: #fff;
          transition: border-color .18s, box-shadow .18s;
          overflow: visible;
        }
        .phone-field-wrap:focus-within {
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(211,47,47,0.10);
        }

        /* ── Country trigger button ── */
        .country-trigger {
          display: flex;
          align-items: center;
          gap: 0.3rem;
          padding: 0 0.65rem 0 0.75rem;
          background: #F7F8FA;
          border: none;
          border-right: 1.5px solid var(--border-subtle);
          border-radius: 0.75rem 0 0 0.75rem;
          cursor: pointer;
          min-height: 3.25rem;
          flex-shrink: 0;
          -webkit-tap-highlight-color: transparent;
          transition: background .15s;
          white-space: nowrap;
        }
        .country-trigger:hover { background: #ECEEF1; }
        .country-trigger:active { background: #E3E6EA; }
        .country-trigger-flag {
          font-size: 1.3rem;
          line-height: 1;
        }
        .country-trigger-code {
          font-size: 0.9rem;
          font-weight: 700;
          color: var(--text-main);
          letter-spacing: 0.01em;
        }
        .country-trigger-chevron {
          width: 0.9rem;
          height: 0.9rem;
          stroke: var(--text-dim);
          transition: transform .2s;
          flex-shrink: 0;
        }
        .country-trigger[aria-expanded="true"] .country-trigger-chevron {
          transform: rotate(180deg);
        }

        /* ── Phone number input ── */
        .phone-number-input {
          flex: 1;
          min-width: 0;
          border: none;
          outline: none;
          font-size: 1.05rem;
          font-family: inherit;
          font-weight: 500;
          color: var(--text-main);
          background: transparent;
          padding: 0.9rem 1rem 0.9rem 0.85rem;
          box-sizing: border-box;
          -webkit-appearance: none;
          letter-spacing: 0.01em;
        }
        .phone-number-input::placeholder { color: #B0B7C3; font-weight: 400; }

        /* ── Other-dial inline input ── */
        .other-dial-inline {
          display: flex;
          align-items: center;
          padding: 0 0.1rem;
          border-right: 1.5px solid var(--border-subtle);
          background: #F7F8FA;
        }
        .other-dial-inline input {
          width: 3.25rem;
          border: none;
          outline: none;
          background: transparent;
          font-size: 0.9rem;
          font-weight: 700;
          font-family: ui-monospace, monospace;
          color: var(--text-main);
          text-align: center;
          padding: 0.9rem 0.2rem;
        }
        .other-dial-inline input::placeholder { color: #B0B7C3; font-weight: 400; }

        /* ── Dropdown ── */
        .country-dropdown {
          position: absolute;
          top: calc(100% + 6px);
          left: 0;
          width: 100%;
          max-width: 320px;
          background: var(--surface);
          border: 1.5px solid var(--border-subtle);
          border-radius: 0.9rem;
          box-shadow: 0 8px 28px rgba(0,0,0,0.12);
          z-index: 200;
          overflow: hidden;
          animation: dd-in .15s cubic-bezier(.4,0,.2,1) both;
        }
        @keyframes dd-in {
          from { opacity: 0; transform: translateY(-4px) scale(.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .dd-search {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.65rem 0.85rem;
          border-bottom: 1px solid var(--border-subtle);
        }
        .dd-search-icon {
          width: 0.95rem; height: 0.95rem;
          stroke: var(--text-dim); flex-shrink: 0;
        }
        .dd-search input {
          flex: 1; border: none; outline: none;
          font-family: inherit;
          font-size: 0.875rem;
          color: var(--text-main);
          background: transparent;
        }
        .dd-search input::placeholder { color: #B0B7C3; }
        .dd-list {
          max-height: 220px;
          overflow-y: auto;
          overscroll-behavior: contain;
        }
        .dd-item {
          display: flex;
          align-items: center;
          gap: 0.65rem;
          padding: 0.7rem 0.85rem;
          cursor: pointer;
          transition: background .1s;
          -webkit-tap-highlight-color: transparent;
        }
        .dd-item:hover, .dd-item:focus { background: #F7F8FA; outline: none; }
        .dd-item.dd-item-active { background: #FFF0F0; }
        .dd-item-flag { font-size: 1.25rem; line-height: 1; flex-shrink: 0; }
        .dd-item-name { flex: 1; font-size: 0.875rem; font-weight: 500; color: var(--text-main); }
        .dd-item-code { font-size: 0.8rem; font-weight: 600; color: var(--text-dim); }
        .dd-empty {
          padding: 1.1rem 0.85rem;
          font-size: 0.85rem;
          color: var(--text-dim);
          text-align: center;
        }
        .dd-other-item { border-top: 1px solid var(--border-subtle); }

        /* ── Error ── */
        .error-text {
          min-height: 1.2rem;
          font-size: .8rem;
          font-weight: 500;
          color: var(--error);
          margin-top: .5rem;
          margin-bottom: .75rem;
        }

        /* ── Submit ── */
        .confirm-wrap {
          display: flex; justify-content: center;
          margin-top: .35rem; margin-bottom: .25rem;
        }
        .confirm-button {
          background: var(--primary); color: #FFF;
          border: none; border-radius: var(--radius-full);
          font-size: 1.05rem; font-weight: 600; line-height: 1.2;
          padding: 1rem 1.5rem; min-width: 200px; min-height: 48px;
          cursor: pointer; font-family: inherit;
          transition: background .18s, opacity .18s, transform .12s;
          -webkit-tap-highlight-color: transparent;
          display: inline-flex; align-items: center; justify-content: center; gap: .5rem;
        }
        .confirm-button:hover:not(:disabled) { background: #B71C1C; }
        .confirm-button:active:not(:disabled) { transform: scale(.98); }
        .confirm-button:focus-visible { box-shadow: 0 0 0 3px rgba(211,47,47,0.20); }
        .confirm-button:disabled { opacity: .55; cursor: not-allowed; }
        .confirm-button__spinner {
          width: 16px; height: 16px;
          border: 2px solid rgba(255,255,255,0.4);
          border-top-color: #fff;
          border-radius: 50%;
          animation: pf-spin 0.8s linear infinite;
        }
        @keyframes pf-spin { to { transform: rotate(360deg); } }

        .contact-link {
          margin-top: auto; text-align: center;
          font-size: .8rem; color: var(--text-main); font-weight: 400;
          padding: 0 1rem calc(1.5rem + env(safe-area-inset-bottom));
          box-sizing: border-box;
        }
        .contact-link a { color: var(--primary); font-weight: 600; text-decoration: none; }

        @media (min-width: 480px) {
          .inner-wrap { padding: 5.5rem 2rem 4rem; }
          .main-headline { font-size: 1.4rem; }
          .confirm-button { min-width: 220px; font-size: 1rem; }
        }
        @media (min-width: 768px) {
          .main-headline { font-size: 1.5rem; }
        }
      `}</style>

      <div className="page-shell">
        <div className="inner-wrap">
          <FormBackButton
            onClick={() => {
              const variant = getSignupVariant();
              if (isFirstStep('/telefone', variant)) {
                navigate('/');
                return;
              }
              const prev = prevReachableSignupRoute('telefone', variant);
              navigate(prev || '/cpf');
            }}
          />

          <ProgressBar {...signupUxProgress('/telefone', getSignupVariant())} />

          <div className="header-block">
            {guided ? <SignupGreeting fullName={readSignupFullName()} /> : null}
            <h1 className="main-headline deco-headline">Qual é o seu telefone?</h1>
            <DecoDivider variant="chevron" />
            <p className="sub-headline" id="patient-phone-subtitle">
              {guided ? 'Com DDD.' : 'Informe DDD + número do seu telefone.'}
            </p>
          </div>

          <form
            className="form-card"
            onSubmit={(e) => { e.preventDefault(); handleConfirm(); }}
            noValidate
          >
            {guided ? (
              <SignupStepHeading
                title={signupStepTitle('/telefone')}
                subtitle="Com DDD."
              />
            ) : null}
            <label htmlFor="patient-phone" className="sr-only">Telefone com DDD</label>

            <div className="phone-field-wrap">
              {/* ── Country trigger ── */}
              <button
                ref={triggerRef}
                type="button"
                className="country-trigger"
                aria-haspopup="listbox"
                aria-expanded={dropdownOpen}
                aria-label={'País do telefone: ' + currentDialLabel}
                onClick={() => { setDropdownOpen((o) => !o); }}
              >
                <span className="country-trigger-flag" aria-hidden="true">{currentFlag}</span>
                <span className="country-trigger-code">{currentDialLabel}</span>
                <svg className="country-trigger-chevron" viewBox="0 0 24 24" fill="none">
                  <polyline points="6 9 12 15 18 9" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {/* ── Dropdown ── */}
              {dropdownOpen && (
                <div className="country-dropdown" ref={dropdownRef} role="listbox" aria-label="Selecione o país">
                  <div className="dd-search">
                    <svg className="dd-search-icon" viewBox="0 0 24 24" fill="none">
                      <circle cx="11" cy="11" r="7" strokeWidth="2" />
                      <line x1="16.5" y1="16.5" x2="22" y2="22" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <input
                      ref={searchRef}
                      type="text"
                      placeholder="Buscar país ou código..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Escape') { setDropdownOpen(false); setSearch(''); } }}
                    />
                  </div>
                  <div className="dd-list">
                    {filteredCountries.length === 0 && (
                      <div className="dd-empty">Nenhum país encontrado.</div>
                    )}
                    {filteredCountries.map((c) => (
                      <div
                        key={c.dial}
                        role="option"
                        aria-selected={dialSelect === c.dial}
                        className={'dd-item' + (dialSelect === c.dial ? ' dd-item-active' : '')}
                        tabIndex={0}
                        onMouseDown={(e) => { e.preventDefault(); selectCountry(c.dial); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectCountry(c.dial); } }}
                      >
                        <span className="dd-item-flag" aria-hidden="true">{c.flag}</span>
                        <span className="dd-item-name">{c.label}</span>
                        <span className="dd-item-code">+{c.dial}</span>
                      </div>
                    ))}
                    {(!search) && (
                      <div
                        role="option"
                        aria-selected={dialSelect === OTHER_DIAL_VALUE}
                        className={'dd-item dd-other-item' + (dialSelect === OTHER_DIAL_VALUE ? ' dd-item-active' : '')}
                        tabIndex={0}
                        onMouseDown={(e) => { e.preventDefault(); selectCountry(OTHER_DIAL_VALUE); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectCountry(OTHER_DIAL_VALUE); } }}
                      >
                        <span className="dd-item-flag" aria-hidden="true">{'\uD83C\uDF10'}</span>
                        <span className="dd-item-name">Outro país</span>
                        <span className="dd-item-code">DDI</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── Other-dial inline ── */}
              {dialSelect === OTHER_DIAL_VALUE && (
                <div className="other-dial-inline">
                  <input
                    ref={otherDialRef}
                    type="text"
                    inputMode="numeric"
                    placeholder="+DDI"
                    value={otherDial}
                    onChange={handleOtherDialChange}
                    maxLength={3}
                    autoComplete="off"
                    aria-label="Código DDI"
                    aria-invalid={touched && dialSelect === OTHER_DIAL_VALUE && !effectiveDial}
                  />
                </div>
              )}

              {/* ── Phone number input ── */}
              <input
                id="patient-phone"
                ref={inputRef}
                type="tel"
                inputMode="numeric"
                className="phone-number-input"
                placeholder={isBrazilDial ? '(41) 99999-9999' : 'Número com DDD'}
                value={phone}
                onChange={handleChange}
                onPaste={handlePaste}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleConfirm(); } }}
                onFocus={(e) => {
                  const node = e.currentTarget;
                  setTimeout(() => { try { node.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch {} }, 220);
                }}
                maxLength={nationalMaxLen}
                autoComplete={isBrazilDial ? 'tel-national' : 'tel'}
                enterKeyHint="done"
                aria-invalid={touched && !isValid}
                aria-describedby="patient-phone-subtitle patient-phone-error"
                aria-required="true"
              />
              {guided && <FieldValidCheck show={isValid} label="Telefone válido" />}
            </div>

            <div className="error-text" id="patient-phone-error" role={touched && !isValid ? 'alert' : undefined}>
              {getErrorMessage()}
            </div>

            {inlineTerms ? <SignupTermsInlineNotice /> : null}
            {acceptError ? (
              <p
                role="alert"
                style={{
                  margin: '0 0 0.75rem',
                  textAlign: 'center',
                  fontSize: '0.8rem',
                  lineHeight: 1.45,
                  color: '#B91C1C',
                }}
              >
                {acceptError}
              </p>
            ) : null}

            <div className="confirm-wrap">
              <button type="submit" className="confirm-button" disabled={!isValid || isSubmitting || !!blockedMessage}>
                {signupCta(inlineTerms ? 'telefoneComTermos' : 'telefone', {
                  guided,
                  submitting: isSubmitting,
                })}
              </button>
            </div>
          </form>
        </div>
      </div>

      {inlineTerms ? (
        <ImageFraudTacModal
          open={!!tacInfo && !blockedMessage}
          apiBaseUrl={SIGNUP_TERMS_API_URL}
          patientId={termsPatientId}
          tacInfo={tacInfo}
          onAccepted={async () => {
            setTacInfo(null);
            await acceptTerms({ source: 'phone_inline_tac' });
          }}
          onBlocked={(msg) => {
            setTacInfo(null);
            setBlockedMessage(msg || SIGNUP_TERMS_BLOCKED_DEFAULT);
          }}
        />
      ) : null}

      {blockedMessage && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="phone-terms-blocked-title"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10500,
            background: 'rgba(15, 23, 42, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '1rem',
              padding: '1.5rem 1.5rem 1.25rem',
              maxWidth: 420,
              width: '100%',
              boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
            }}
          >
            <h2
              id="phone-terms-blocked-title"
              style={{ margin: '0 0 0.6rem', fontSize: '1.05rem', fontWeight: 700, color: '#B91C1C' }}
            >
              Acesso bloqueado
            </h2>
            <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: '#334155', lineHeight: 1.55 }}>
              {blockedMessage}
            </p>
            <button
              type="button"
              onClick={() => navigate('/')}
              style={{
                width: '100%',
                padding: '0.7rem',
                borderRadius: '0.5rem',
                border: 'none',
                background: '#1E293B',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.875rem',
                cursor: 'pointer',
              }}
            >
              Entendi
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default PhoneForm;
